import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import api from '../utils/api';
import { Activity, Wifi, WifiOff, RefreshCw } from 'lucide-react';

const HypervisorPingGraph = ({ hypervisorId, compact = false }) => {
  const [pingData, setPingData] = useState([]);
  const [timeRange, setTimeRange] = useState(60); // Default: 60 minutes
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    current: null,
    average: 0,
    min: 0,
    max: 0,
    uptime: 100
  });

  const timeRangeOptions = [
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 360, label: '6 hours' },
    { value: 1440, label: '24 hours' }
  ];

  useEffect(() => {
    fetchPingData();
    // Refresh every minute
    const interval = setInterval(fetchPingData, 60000);
    return () => clearInterval(interval);
  }, [hypervisorId, timeRange]);

  const fetchPingData = async () => {
    try {
      const response = await api.get(`/hypervisors/${hypervisorId}/ping-history?minutes=${timeRange}`);
      const history = response.data.history || [];
      
      // Process data for chart
      const chartData = history.map(item => ({
        timestamp: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        fullTimestamp: new Date(item.timestamp),
        responseTime: item.response_time,
        status: item.status
      }));
      
      setPingData(chartData);
      
      // Calculate statistics
      const validPings = history.filter(item => item.response_time !== null);
      const totalPings = history.length;
      const successfulPings = validPings.length;
      
      if (validPings.length > 0) {
        const responseTimes = validPings.map(item => item.response_time);
        const avg = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
        const min = Math.min(...responseTimes);
        const max = Math.max(...responseTimes);
        const current = history.length > 0 ? history[history.length - 1].response_time : null;
        const uptimePercent = (successfulPings / totalPings) * 100;
        
        setStats({
          current: current ? Math.round(current) : null,
          average: Math.round(avg),
          min: Math.round(min),
          max: Math.round(max),
          uptime: Math.round(uptimePercent * 100) / 100
        });
      } else {
        setStats({
          current: null,
          average: 0,
          min: 0,
          max: 0,
          uptime: 0
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch ping data:', error);
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-semibold">{data.fullTimestamp.toLocaleString()}</p>
          {data.status === 'up' ? (
            <p className="text-sm text-green-600">
              Response: <span className="font-bold">{data.responseTime}ms</span>
            </p>
          ) : (
            <p className="text-sm text-red-600 font-bold">Down</p>
          )}
        </div>
      );
    }
    return null;
  };

  const getStatusColor = () => {
    if (stats.current === null) return 'text-red-500';
    if (stats.current < 50) return 'text-green-500';
    if (stats.current < 150) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getStatusIcon = () => {
    if (stats.current === null) return <WifiOff className="w-4 h-4" />;
    return <Wifi className="w-4 h-4" />;
  };

  if (compact) {
    // Compact version for card view
    return (
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">Ping Monitor</span>
          </div>
          {stats.current !== null ? (
            <Badge variant="default" className="bg-green-500">
              {stats.current}ms
            </Badge>
          ) : (
            <Badge variant="destructive">Offline</Badge>
          )}
        </div>
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pingData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <Line 
                type="monotone" 
                dataKey="responseTime" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
              <YAxis hide />
              <XAxis hide />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>Uptime: {stats.uptime}%</span>
          <span>Avg: {stats.average}ms</span>
        </div>
      </div>
    );
  }

  // Full version for metrics dialog
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Ping Monitor</CardTitle>
          </div>
          <Select value={timeRange.toString()} onValueChange={(value) => setTimeRange(parseInt(value))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map(option => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">Current</div>
                <div className={`text-xl font-bold flex items-center gap-1 ${getStatusColor()}`}>
                  {getStatusIcon()}
                  {stats.current !== null ? `${stats.current}ms` : 'Down'}
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">Average</div>
                <div className="text-xl font-bold text-slate-800">{stats.average}ms</div>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">Min</div>
                <div className="text-xl font-bold text-green-600">{stats.min}ms</div>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">Max</div>
                <div className="text-xl font-bold text-red-600">{stats.max}ms</div>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-xs text-slate-600 mb-1">Uptime</div>
                <div className="text-xl font-bold text-blue-600">{stats.uptime}%</div>
              </div>
            </div>

            {/* Graph */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pingData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={100} stroke="#f59e0b" strokeDasharray="3 3" label="100ms" />
                  <Line 
                    type="monotone" 
                    dataKey="responseTime" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 2 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 text-xs text-slate-500 text-center">
              Data refreshes every minute • Last {pingData.length} pings shown
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HypervisorPingGraph;
